/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import { removeHoldingFromPortfolio } from '@actions/portfolio-tracker';
import { DataShareStatus } from '@g17eco/types/dataShare';
import { DataScopeAccess, DataShareScopeView } from '@g17eco/types/surveyDataScope';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledDropdownProps } from 'reactstrap';
import { InitiativeType } from '@g17eco/types/initiative';
import { InitiativeCompany } from '@g17eco/types/initiativeSurvey';
import { getViewAccess, hasFullScopeAccess, isNoAccess, isViewShared } from '@utils/dataShare';
import { AppCode } from '@g17eco/types/app';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

export const NO_ACCESS_MESSAGE = 'Company is not open for data sharing or messaging. Unable to process requests.';

type OnShareFn = (company: Pick<InitiativeCompany, '_id' | 'name' | 'requestedDataShares'>) => void;
type SendMessageFn = (company: Pick<InitiativeCompany, '_id' | 'name'>) => void

interface DataShareButtonProps {
  portfolioId: string;
  holdingId: string;
  company?: Pick<InitiativeCompany, '_id' | 'name' | 'type' | 'dataShare' | 'requestedDataShares' | 'appConfigCode'>;
  onShare: OnShareFn;
  disabled?: boolean;
}

interface MenuButtonProps extends DataShareButtonProps {
  isPortfolio?: boolean;
  holdingId: string;
  handleReload: () => void;
  setSaving: (saving: boolean) => void;
  dropdownProps?: UncontrolledDropdownProps;
  handleSendMessage: SendMessageFn;
}

const DataShareButton = ({ company, onShare, disabled }: DataShareButtonProps) => {

  if (!company || company.type !== InitiativeType.initiative) {
    return null;
  }

  const dataShare = company.requestedDataShares;
  const hasData = dataShare && dataShare.length > 0
  const icon = <i className='fal fa-share-from-square fa-fw mr-2'/>


  if (!hasData) {
    return <>
      <DropdownItem size='sm' disabled={disabled || hasData} onClick={() => onShare(company)}>
        {icon}Request data access
      </DropdownItem>
      <DropdownItem divider />
    </>;
  }

  const shareScopeViewsAccess = getViewAccess(company);

  if (shareScopeViewsAccess === DataScopeAccess.Full && hasFullScopeAccess(company)) {
    return (
      <>
        <DropdownItem size='sm' disabled>
          {icon}Access granted
        </DropdownItem>
        <DropdownItem divider />
      </>
    );
  }

  if (dataShare.some((s) => s.status === DataShareStatus.Pending)) {
    return (
      <>
        <DropdownItem size='sm' disabled>
          {icon}Access request pending
        </DropdownItem>

        <DropdownItem divider />
      </>
    );
  }

  return (
    <>
      <DropdownItem size='sm' onClick={() => onShare(company)}>
        {icon}Request additional access
      </DropdownItem>
      <DropdownItem divider />
    </>
  );
};

export const MenuButton = (props: MenuButtonProps) => {

  const {
    portfolioId,
    holdingId,
    disabled,
    setSaving,
    handleReload,
    company,
    onShare,
    isPortfolio,
    dropdownProps,
    handleSendMessage,
  } = props;

  const handleRemove = async (holdingId: string) => {
    setSaving(true);
    try {
      await removeHoldingFromPortfolio(portfolioId, holdingId);
      handleReload();
    } catch (e) {
      console.log(e);
    }
    setSaving(false);
  }

  const CompanyDropdownItem = (props: any) => isPortfolio ? null : <DropdownItem {...props} />;

  if (!company) {
    return null;
  }

  return (
    <UncontrolledDropdown {...dropdownProps}>
      <DropdownToggle outline={true} color='secondary' className='px-2'>
        <i className='fas fa-bars' />
      </DropdownToggle>
      <DropdownMenu container='body'>
        <CompanyDropdownItem size='sm' disabled={disabled || !isViewShared(company, DataShareScopeView.Messaging)} onClick={() => handleSendMessage(company)}>
          <i className='fal fa-envelope fa-fw mr-2' />Send a message
        </CompanyDropdownItem>
        <CompanyDropdownItem divider={true} />
        <SimpleTooltip text={isNoAccess(company) ? NO_ACCESS_MESSAGE : undefined}>
          <DataShareButton
            portfolioId={portfolioId}
            holdingId={holdingId}
            company={company}
            onShare={onShare}
            disabled={disabled || isNoAccess(company) || company.appConfigCode === AppCode.MaterialityTracker}
          />
        </SimpleTooltip>
        <DropdownItem disabled={disabled} size='sm' onClick={() => handleRemove(holdingId)}>
          <i className='fa fa-xmark fa-fw mr-2 text-ThemeDangerMedium'/>Remove from portfolio
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}
